import { Injectable, computed, inject, signal } from '@angular/core';
import { VideoSearchQuery, VideoSearchSortBy, VideoInfoDTO } from '@solverml/api';
import { FilterModel } from 'src/app/features/handball/video-quering/model/filter-model';
import { VideoSearchResultListPaginated } from 'src/app/features/video/model/video-search-result.model';
import { VideoStreamingService } from 'src/app/features/video/controller/video-streaming.service';

export interface AnalysisState {
    searchQuery: string;
    filters: FilterModel | null;
    sortBy: VideoSearchSortBy[] | null;
    currentPage: number;
    viewMode: 'grid' | 'list';
    videos: VideoSearchResultListPaginated | null;
}

const initialState: AnalysisState = {
    searchQuery: '',
    filters: null,
    sortBy: null,
    currentPage: 0,
    viewMode: 'grid',
    videos: null,
};

@Injectable({ providedIn: 'root' })
export class AnalysisStore {
    private readonly videoStreamingService = inject(VideoStreamingService);

    private readonly _state = signal<AnalysisState>(initialState);

    // Selectors
    readonly searchQuery = computed(() => this._state().searchQuery);
    readonly filters = computed(() => this._state().filters);
    readonly sortBy = computed(() => this._state().sortBy);
    readonly currentPage = computed(() => this._state().currentPage);
    readonly viewMode = computed(() => this._state().viewMode);
    readonly videos = computed(() => this._state().videos);

    readonly pageSize = computed(() => this.viewMode() === 'list' ? 50 : 12);

    hydrate(): void {
        try {
            const savedStateStr = sessionStorage.getItem('analysisState');
            if (savedStateStr) {
                const state = JSON.parse(savedStateStr);
                this._state.update(s => ({
                    ...s,
                    searchQuery: state.searchQuery !== undefined ? state.searchQuery : s.searchQuery,
                    // Handle both 'appliedFilters' (old) and 'filters' (new) for backwards/forwards compatibility temporarily
                    filters: state.appliedFilters !== undefined ? state.appliedFilters : state.filters !== undefined ? state.filters : s.filters,
                    sortBy: state.appliedSortBy !== undefined ? state.appliedSortBy : state.sortBy !== undefined ? state.sortBy : s.sortBy,
                    currentPage: state.currentPage !== undefined ? state.currentPage : s.currentPage,
                    viewMode: state.viewMode !== undefined ? state.viewMode : s.viewMode,
                }));
            }
        } catch (e) {
            console.error('Error hydrating analysis state', e);
        }
    }

    private saveState(): void {
        const s = this._state();
        const stateToSave = {
            searchQuery: s.searchQuery,
            appliedFilters: s.filters, // Keeping old name for sessionStorage compatibility for now
            appliedSortBy: s.sortBy,
            currentPage: s.currentPage,
            viewMode: s.viewMode
        };
        sessionStorage.setItem('analysisState', JSON.stringify(stateToSave));
    }

    updateSearch(text: string): void {
        this._state.update(s => ({ ...s, searchQuery: text, currentPage: 0 }));
        this.saveState();
        this.loadVideos();
    }

    applyFilters(filters: FilterModel | null): void {
        this._state.update(s => ({ ...s, filters, currentPage: 0 }));
        this.saveState();
        this.loadVideos();
    }

    applySortBy(sortBy: VideoSearchSortBy[] | null): void {
        this._state.update(s => ({ ...s, sortBy, currentPage: 0 }));
        this.saveState();
        this.loadVideos();
    }

    setPage(pageIndex: number): void {
        this._state.update(s => ({ ...s, currentPage: pageIndex }));
        this.saveState();
        this.loadVideos();
    }

    toggleViewMode(): void {
        this._state.update(s => ({ ...s, viewMode: s.viewMode === 'list' ? 'grid' : 'list', currentPage: 0 }));
        this.saveState();
        this.loadVideos();
    }

    private _buildApiSearchQuery(): VideoSearchQuery {
        const s = this._state();
        const apiQuery: VideoSearchQuery = {
            search_query: s.searchQuery || undefined,
            page: s.currentPage,
            page_size: this.pageSize(),
            sort_by: s.sortBy || undefined,
        };

        if (s.filters) {
            apiQuery.category_ids = s.filters.category ? [Number(s.filters.category)] : undefined;
            apiQuery.competition_ids = s.filters.competition ? [Number(s.filters.competition)] : undefined;
            apiQuery.gender = s.filters.gender ? [s.filters.gender as VideoInfoDTO.GenderEnum] : undefined;
            apiQuery.date_start = s.filters.dateStart || undefined;
            apiQuery.date_end = s.filters.dateEnd || undefined;
        }

        return apiQuery;
    }

    async loadVideos(): Promise<void> {
        this._state.update(s => ({ ...s, videos: null }));
        await new Promise(resolve => setTimeout(resolve, 100));

        const apiQuery = this._buildApiSearchQuery();

        try {
            const videos = await this.videoStreamingService.searchVideos(apiQuery);
            this._state.update(s => ({ ...s, videos }));
        } catch (error) {
            console.error("Error loading videos", error);
        }
    }
}
